// React imports
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

// Third-party library imports
import { yupResolver } from "@hookform/resolvers/yup";

// Utility and hook imports
import useTitle from "../../../../hooks/useTitle";
import { clearSession } from "../../../../api/onboarding";
import { useLocation, useNavigate } from "react-router-dom";
import { GeaoPaths } from "../../constants/GeaoPaths";
import { ClientConfigurationContext } from "../../../../context/ClientConfigurationContextProvider";

// Layout and shared components
import CobrandLayout from "../shared/Layout/CobrandLayout";

// GDS components
import InputBox from "../../../gds/InputBox";

// Local components and hooks
import AgencyNameField from "./AgencyNameField";
import CaptiveAgentQuestions from "./CaptiveAgentQuestions";
import CreateYourProfileFormFooter from "./CreateYourProfileFormFooter";
import EoPolicyQuestions from "./EoPolicyQuestions";
import ErrorAlerts from "./ErrorAlerts";
import FeinField from "./FeinField";
import NiprNameChangeModal from "./NiprNameChangeModal";
import PersonalInformationFields from "./PersonalInformationFields";
import useCreateYourProfileNationalAccountNavigation from "./useCreateYourProfileNationalAccountNavigation";
import useInterviewInitializer from "./useInterviewInitializer";
import { validationSchema } from "./validation";

// Constants and options
import RoleOptions from "../../constants/options/RoleOptions";
import useGtmTracking from "../../../../hooks/useGtmTracking";

export default function CreateYourProfileNationalAccount() {
    useTitle("Let's Get Started");
    useGtmTracking("Pre-onboarding", "preonboarding_in_progress");

    const { pathname } = useLocation();
    const clientConfiguration = useContext(ClientConfigurationContext);
    const nationalAccountParam = pathname.split(GeaoPaths.PreOnboardingNationalAccountPrefix)[1] ?? '';
    const nationalAccountOption = clientConfiguration?.cobrandedNationalAccounts
        ?.find(o => o.slug.toLowerCase() === nationalAccountParam.toLowerCase());
    const cobrandedNationalAccount = nationalAccountOption?.value ?? null;
    const nationalAccount = nationalAccountOption?.label ?? nationalAccountParam;

    const [niprNameChangeAccepted, setNiprNameChangeAccepted] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (nationalAccountOption?.enabled === false) {
            navigate(GeaoPaths.OnboardingPaused, {
                replace: true,
                state: {
                    partnerName: nationalAccount
                }
            });
        }
    }, [nationalAccountOption, navigate, nationalAccount]);

    const useFormReturn = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            role: "principal",
            soleProprietor: "false",
            lobPersonal: true,
            lobCommercial: true,
            geicoAppointedStates: ["OH"],
            cobrandedNationalAccount
        }
    });

    const {
        register,
        handleSubmit,
        setError,
        control
    } = useFormReturn;

    const {
        initializeInterviews,
        initializing,
        duplicateProfile,
        npnNotFoundInPdb,
        feinNotFoundInPdb,
        producerNameMismatch,
        agencyNameMismatch,
        duplicateAgent,
        duplicateAgency,
        niprAgentNameChange,
        niprAgencyNameChange,
        agentAssessmentKickout,
        agencyAssessmentKickout,
        parentAgencyMustOnboard,
        agencyOffshoreMustBeApproved
    } = useInterviewInitializer(
        setNiprNameChangeAccepted,
        { CobrandedNationalAccount: cobrandedNationalAccount },
        () => navigate(GeaoPaths.TechnicalDifficulties)
    );

    useCreateYourProfileNationalAccountNavigation({
        duplicateAgency,
        duplicateAgent,
        niprAgencyNameChange,
        niprAgentNameChange,
        niprNameChangeAccepted,
        agencyAssessmentKickout,
        agentAssessmentKickout,
        parentAgencyMustOnboard,
        agencyOffshoreMustBeApproved
    });

    useEffect(() => { register("interestedLob") }, []);

    function onSubmit(values) {
        clearSession(() => { initializeInterviews(values); });
    }

    return (
        <CobrandLayout partnerName={nationalAccount} header="Let's Get Started">
            <div style={{ width: "750px" }}>
                <ErrorAlerts
                    duplicateProfile={duplicateProfile}
                    npnNotFoundInPdb={npnNotFoundInPdb}
                    feinNotFoundInPdb={feinNotFoundInPdb}
                    producerNameMismatch={producerNameMismatch}
                    agencyNameMismatch={agencyNameMismatch}
                    parentAgencyMustOnboard={parentAgencyMustOnboard}
                    agencyOffshoreMustBeApproved={agencyOffshoreMustBeApproved}
                    setError={setError}
                />
            </div>

            <FormProvider {...useFormReturn}>
                <form className="form geao-form" onSubmit={handleSubmit(onSubmit)} style={{ width: "750px" }}>
                    <h2 className="margin-bottom">General Information</h2>

                    <PersonalInformationFields />

                    {/* Role: defaulted to Principal/Owner and disabled */}
                    <div className="flex-row">
                        <div className="form-field">
                            <label htmlFor="role" className="text">
                                What role do you play in the agency?<span className="required">*</span>
                            </label>
                            <div className="select-box">
                                <select id="role" disabled {...register("role")}>
                                    {RoleOptions.map(option => (
                                        <option value={option.value} key={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-field">
                            <InputBox
                                control={control}
                                name="npn"
                                mask="000000000"
                                fullWidth
                                label={<>Individual National Producer Number (NPN)<span className="required">*</span></>}
                            />
                        </div>
                    </div>

                    <h2 className="margin-bottom">Agency Information</h2>

                    <AgencyNameField />

                    {/* Sole Proprietor: defaulted to No and hidden */}
                    <input type="hidden" {...register("soleProprietor")} value="false" />

                    <FeinField />

                    {/* Lines of business: defaulted to Personal and Commercial and hidden */}
                    <input type="hidden" {...register("lobPersonal")} value="true" />
                    <input type="hidden" {...register("lobCommercial")} value="true" />

                    {/* Appointed states: defaulted to ["OH"] via defaultValues — no hidden input needed */}

                    {/* Cobranded national account: defaulted from URL and hidden */}
                    <input type="hidden" {...register("cobrandedNationalAccount")} value={cobrandedNationalAccount ?? ""} />

                    <CaptiveAgentQuestions />

                    <EoPolicyQuestions />

                    <CreateYourProfileFormFooter initializing={initializing} />
                </form>
            </FormProvider>

            <NiprNameChangeModal
                niprAgentNameChange={niprAgentNameChange}
                niprAgencyNameChange={niprAgencyNameChange}
                setNiprNameChangeAccepted={setNiprNameChangeAccepted}
            />
        </CobrandLayout>
    );
}
